import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import type { DatesSetArg, EventClickArg } from '@fullcalendar/core';
import { Plus, List as ListIcon, CalendarDays, Loader2 } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { api } from '@/utils/api';
import {
  fetchCalendar, completeTask, postponeTask,
  toFullCalendarEvent, type CalendarItem, type CalendarScope,
} from './calendarApi';
import { TaskPopover } from './TaskPopover';
import { NewTaskModal, type UserOption } from './NewTaskModal';
import './calendar.css';

const CAL = {
  card: '#FFFFFF', line: '#E7E6DF', ink: '#14140F', muted: '#6B6B60',
  green: '#2FA037', greenDeep: '#1C7126', greenSoft: '#EAF6EA', surf: '#F4F3EC',
  blue: '#2A55B5', amber: '#F59E0B',
};

const SCOPE_OPTIONS: { value: CalendarScope; label: string }[] = [
  { value: 'own', label: 'My calendar' },
  { value: 'team', label: 'My team' },
  { value: 'all', label: 'All sales' },
];

const LEGEND: { type: CalendarItem['type']; label: string; color: string }[] = [
  { type: 'task', label: 'Task', color: CAL.green },
  { type: 'meeting', label: 'Meeting', color: CAL.blue },
  { type: 'followup', label: 'Follow-up', color: CAL.amber },
];

export function CalendarPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const roleName = user?.role?.name ?? '';

  const calRef = useRef<FullCalendar | null>(null);
  const [range, setRange] = useState<{ from: string; to: string } | null>(null);
  const [scope, setScope] = useState<CalendarScope>('own');
  const [items, setItems] = useState<CalendarItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [popover, setPopover] = useState<{ item: CalendarItem; anchor: { x: number; y: number } } | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createDay, setCreateDay] = useState<string | undefined>(undefined);
  const [users, setUsers] = useState<UserOption[]>([]);

  useEffect(() => {
    api.get('/auth/users').then((r) => setUsers(r.data?.data ?? [])).catch(() => {});
  }, []);

  const load = useCallback(async (from: string, to: string, sc: CalendarScope) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchCalendar({ from, to, scope: sc });
      setItems(res.data ?? []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load calendar');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (range) load(range.from, range.to, scope);
  }, [range, scope, load]);

  const handleDatesSet = (arg: DatesSetArg) => {
    const from = arg.startStr.slice(0, 10);
    // FullCalendar's end is exclusive — step back one day for inclusive 'to'.
    const endD = new Date(arg.end);
    endD.setDate(endD.getDate() - 1);
    const to = `${endD.getFullYear()}-${String(endD.getMonth() + 1).padStart(2, '0')}-${String(endD.getDate()).padStart(2, '0')}`;
    setRange((prev) => (prev && prev.from === from && prev.to === to ? prev : { from, to }));
  };

  const events = useMemo(() => items.map(toFullCalendarEvent), [items]);

  const handleEventClick = (arg: EventClickArg) => {
    const item = arg.event.extendedProps.item as CalendarItem;
    const rect = arg.el.getBoundingClientRect();
    setPopover({ item, anchor: { x: rect.left + rect.width / 2, y: rect.bottom } });
  };

  const refresh = useCallback(() => {
    if (range) load(range.from, range.to, scope);
  }, [range, scope, load]);

  async function onComplete(item: CalendarItem) {
    await completeTask(item.id);
    setItems((prev) => prev.map((it) => (it.id === item.id && it.type === 'task' ? { ...it, status: 'done', overdue: false } : it)));
    setPopover(null);
  }
  async function onPostpone(item: CalendarItem, scheduledDate: string) {
    await postponeTask(item.id, scheduledDate);
    setPopover(null);
    refresh();
  }
  function onOpenLink(item: CalendarItem) {
    setPopover(null);
    if (item.type === 'followup' && item.linkId) navigate(`/leads?lead=${item.linkId}`);
    else if (item.type === 'meeting' && item.linkId) navigate(`/deals/${item.linkId}`);
  }

  const monthTitle = useMemo(() => {
    const apiCal = calRef.current?.getApi();
    return apiCal ? apiCal.view.title : '';
  }, [range]);

  const go = (dir: 'prev' | 'next' | 'today') => {
    const apiCal = calRef.current?.getApi();
    if (!apiCal) return;
    if (dir === 'prev') apiCal.prev();
    else if (dir === 'next') apiCal.next();
    else apiCal.today();
  };

  return (
    <div className="stellar-cal" style={{ background: CAL.card, borderRadius: 14, border: `1px solid ${CAL.line}`, overflow: 'hidden' }}>
      {/* top bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${CAL.line}`, gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: CAL.ink, letterSpacing: -0.5, whiteSpace: 'nowrap' }}>{monthTitle || 'Calendar'}</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => go('prev')} aria-label="Previous month" style={navBtn}>‹</button>
            <button onClick={() => go('next')} aria-label="Next month" style={navBtn}>›</button>
          </div>
          <button onClick={() => go('today')} style={{ padding: '7px 14px', borderRadius: 9, background: CAL.greenSoft, color: CAL.greenDeep, fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer' }}>Today</button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {/* List ⇄ Calendar */}
          <div style={{ display: 'inline-flex', background: CAL.surf, borderRadius: 10, padding: 3 }}>
            <button onClick={() => navigate('/tasks')} style={toggleBtn(false)}><ListIcon size={14} /> List</button>
            <span style={toggleBtn(true)}><CalendarDays size={14} /> Calendar</span>
          </div>

          {/* scope */}
          <select value={scope} onChange={(e) => setScope(e.target.value as CalendarScope)} style={{ border: `1px solid ${CAL.line}`, borderRadius: 9, padding: '8px 11px', fontSize: 13, color: CAL.ink, background: CAL.card, fontWeight: 600 }}>
            {SCOPE_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>

          <button onClick={() => { setCreateDay(undefined); setShowCreate(true); }} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 16px', borderRadius: 11, background: CAL.green, color: '#fff', fontSize: 14, fontWeight: 700, border: 'none', cursor: 'pointer' }}>
            <Plus size={17} /> New task
          </button>
        </div>
      </div>

      {/* legend */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 18, padding: '10px 20px', borderBottom: `1px solid ${CAL.line}`, background: CAL.surf }}>
        {LEGEND.map((l) => (
          <span key={l.type} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12.5, color: CAL.ink, fontWeight: 600 }}>
            <span style={{ width: 9, height: 9, borderRadius: '50%', background: l.color }} /> {l.label}
          </span>
        ))}
        {loading && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: CAL.muted, marginLeft: 'auto' }}><Loader2 size={14} className="animate-spin" /> Loading…</span>}
        {error && <span style={{ fontSize: 12.5, color: '#B91C1C', marginLeft: 'auto' }}>{error}</span>}
      </div>

      {/* calendar */}
      <div style={{ padding: '8px 8px 12px' }}>
        <FullCalendar
          ref={calRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{ left: '', center: '', right: 'dayGridMonth,timeGridWeek,timeGridDay' }}
          buttonText={{ month: 'Month', week: 'Week', day: 'Day' }}
          firstDay={1}
          height="auto"
          dayMaxEvents={3}
          events={events}
          datesSet={handleDatesSet}
          eventClick={handleEventClick}
          dateClick={(arg) => { setCreateDay(arg.dateStr.slice(0, 10)); setShowCreate(true); }}
          eventDisplay="block"
        />
      </div>

      {popover && (
        <TaskPopover
          item={popover.item}
          roleName={roleName}
          anchor={popover.anchor}
          onClose={() => setPopover(null)}
          onComplete={onComplete}
          onPostpone={onPostpone}
          onOpenLink={onOpenLink}
        />
      )}

      {showCreate && (
        <NewTaskModal
          roleName={roleName}
          currentUserId={user?.id}
          users={users}
          defaultScheduledDate={createDay}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); refresh(); }}
        />
      )}
    </div>
  );
}

const navBtn: React.CSSProperties = {
  width: 34, height: 34, borderRadius: 9, border: `1px solid ${CAL.line}`, background: CAL.card,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 18, color: CAL.muted, lineHeight: 1,
};
function toggleBtn(on: boolean): React.CSSProperties {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 12px', borderRadius: 8, fontSize: 12.5, fontWeight: 700,
    background: on ? CAL.card : 'transparent', color: on ? CAL.ink : CAL.muted,
    boxShadow: on ? '0 1px 3px rgba(0,0,0,0.08)' : 'none', border: 'none', cursor: on ? 'default' : 'pointer',
  };
}

export default CalendarPage;
